import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";
import { Spinner, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/seo";
import { Body } from "./Guides";

const UI = {
  en: { eyebrow: "Blog", title: "Ideas for emails people love to open", desc: "Practical advice on newsletters, campaigns, deliverability and growing your list — a fresh article every day.", none: "The first article is on its way.", more: "Load more", back: "All articles", read: "Read article", keep: "Keep reading", cta: "Put it into practice — start free", other: "Lue suomeksi" },
  fi: { eyebrow: "Blogi", title: "Ideoita sähköposteihin, joita avataan mielellään", desc: "Käytännön vinkkejä uutiskirjeisiin, kampanjoihin, toimitettavuuteen ja listan kasvattamiseen — uusi artikkeli joka päivä.", none: "Ensimmäinen artikkeli on tulossa.", more: "Näytä lisää", back: "Kaikki artikkelit", read: "Lue artikkeli", keep: "Jatka lukemista", cta: "Kokeile käytännössä — aloita ilmaiseksi", other: "Read in English" },
};
const day = (iso, lang) => new Date(iso).toLocaleDateString(lang === "fi" ? "fi-FI" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

function PostCard({ p, lang, ui }) {
  return (
    <Link to={`/blog/${p.slug}`} className="surface group flex flex-col rounded-2xl p-6 transition-transform hover:-translate-y-0.5" data-testid={`blog-card-${p.slug}`}>
      <div className="flex items-center gap-1.5 text-xs text-zinc-500"><CalendarDays className="h-3.5 w-3.5" />{day(p.published_at, lang)}</div>
      <h2 className="mt-3 font-display text-lg leading-snug text-white group-hover:text-purple-100">{p.title}</h2>
      <p className="mt-2 flex-1 text-sm text-zinc-400">{p.description}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm text-purple-300">{ui.read}<ArrowRight className="h-4 w-4" /></span>
    </Link>
  );
}

export function Blog() {
  const { lang } = useT();
  const ui = UI[lang] || UI.en;
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState([]);
  useSeo({ title: lang === "fi" ? "Blogi: sähköpostimarkkinoinnin vinkit" : "Blog: email marketing tips and ideas", description: ui.desc, path: "/blog" });
  const { data, loading } = useLoad(() => api.get("/public/blog", { params: { lang, page } }).then((r) => r.data), [lang, page]);
  useEffect(() => { if (data) setPosts((prev) => (data.page === 1 ? data.posts : [...prev, ...data.posts])); }, [data]);
  useEffect(() => { setPage(1); }, [lang]);
  return (
    <div className="relative overflow-x-hidden"><div className="aurora opacity-20" />
      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="eyebrow mb-3">{ui.eyebrow}</div>
        <h1 className="max-w-3xl text-4xl font-semibold text-white sm:text-5xl lg:text-6xl" data-testid="page-title">{ui.title}</h1>
        <p className="mt-4 max-w-2xl text-zinc-400">{ui.desc}</p>
        {loading && !posts.length ? <Spinner /> : !posts.length ? <p className="mt-12 text-zinc-500" data-testid="blog-empty">{ui.none}</p> : (
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">{posts.map((p) => <PostCard key={p.slug} p={p} lang={lang} ui={ui} />)}</div>)}
        {data && posts.length < data.total && <Button variant="outline" className="mt-10" onClick={() => setPage(page + 1)} data-testid="blog-more">{ui.more}</Button>}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { lang } = useT();
  const { data, error, loading } = useLoad(() => api.get(`/public/blog/${slug}`).then((r) => r.data), [slug]);
  const ui = UI[data?.lang || lang] || UI.en;
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useSeo({ title: data?.title || "Blog", description: data?.description || ui.desc, path: `/blog/${slug}`,
    article: data ? { published: data.published_at, modified: data.updated_at, lang: data.lang } : null,
    alternates: data?.alt_slug ? { [data.lang]: `/blog/${slug}`, [data.lang === "en" ? "fi" : "en"]: `/blog/${data.alt_slug}` } : null });
  if (error) return <Navigate to="/blog" replace />;
  if (loading || !data) return <Spinner />;
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:px-8 lg:py-20">
      <article lang={data.lang} data-testid="blog-article">
        <Link to="/blog" className="mb-6 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="blog-back"><ArrowLeft className="h-4 w-4" />{ui.back}</Link>
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500"><span className="eyebrow !mb-0">{ui.eyebrow}</span><span>{day(data.published_at, data.lang)}</span>
          {data.alt_slug && <Link to={`/blog/${data.alt_slug}`} className="text-purple-300 hover:text-purple-200" data-testid="blog-alt-lang">{ui.other}</Link>}</div>
        <h1 className="mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="page-title">{data.title}</h1>
        <p className="mt-4 text-lg text-zinc-400">{data.description}</p>
        <div className="mt-6 text-[15px]"><Body md={data.body} /></div>
        <div className="lux-border mt-14 rounded-2xl p-6 text-center"><Button asChild className="glow-btn" data-testid="blog-cta"><Link to="/register">{ui.cta}</Link></Button></div>
      </article>
      <aside className="space-y-3 lg:sticky lg:top-24 lg:self-start" data-testid="blog-more-list">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{ui.keep}</div>
        {data.more.map((p) => <Link key={p.slug} to={`/blog/${p.slug}`} className="block rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-white/[0.04] hover:text-white">{p.title}</Link>)}
      </aside>
    </div>
  );
}
